<template>
  <div
    class="dropzone-container"
    @dragover="dragover"
    @dragleave="dragleave"
    @drop="drop"
    :style="isDragging && 'border-color: blue;'"
  >
    <input
      type="file"
      :multiple="multiple"
      name="file"
      :id="inputId"
      class="hidden-input"
      @change="onChange"
      ref="file"
      :accept="accept"
    />

    <label :for="inputId" class="file-label text-center">
      <div v-if="isDragging">Drop the file to upload it.</div>
      <div v-else>
        <div class="text-h3">
          <v-icon>mdi-upload</v-icon>
        </div>

        <p>Drop your file here</p>
        <p>or</p>
        <p>click <u>here</u> to select your file.</p>
      </div>
    </label>

    <div class="preview-container mt-4" v-if="files.length">
      <div class="preview-card" v-for="file in files" :key="file.name">
        <div class="d-flex align-center">
          <v-icon class="mr-2">{{ iconFor(file) }}</v-icon>
          <span>{{ file.name }} - {{ Math.round(file.size / 1000) + "kb" }}</span>
        </div>
        <div>
          <button
            class="ml-3"
            type="button"
            @click="remove(files.indexOf(file))"
            title="Remove file"
          >
            <strong>×</strong>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    // Restricts the native picker. Defaults to CSV for the customer import.
    accept: {
      type: String,
      default: ".csv, text/csv",
    },
    multiple: {
      type: Boolean,
      default: false,
    },
  },

  // Parents listen for this to receive the selected File objects.
  emits: ["files-changed"],

  data() {
    return {
      isDragging: false,
      files: [],
      // Unique id so several drop zones can coexist on one page.
      inputId: "fileInput-" + Math.random().toString(36).slice(2, 9),
    };
  },

  methods: {
    onChange() {
      const incomingFiles = Array.from(this.$refs.file.files);

      if (this.multiple) {
        const isDuplicate = this.files.some((existingFile) =>
          incomingFiles.some(
            (file) =>
              file.name === existingFile.name && file.size === existingFile.size
          )
        );
        if (isDuplicate) {
          this.$emit("files-changed", this.files);
          return;
        }
        this.files.push(...incomingFiles);
      } else {
        // Single-file mode: the newest selection replaces the previous one.
        this.files = incomingFiles.slice(0, 1);
      }

      this.$emit("files-changed", this.files);
    },

    dragover(e) {
      e.preventDefault();
      this.isDragging = true;
    },

    dragleave() {
      this.isDragging = false;
    },

    drop(e) {
      e.preventDefault();
      this.$refs.file.files = e.dataTransfer.files;
      this.onChange();
      this.isDragging = false;
    },

    remove(i) {
      this.files.splice(i, 1);
      this.$emit("files-changed", this.files);
    },

    reset() {
      this.files = [];
      if (this.$refs.file) this.$refs.file.value = "";
      this.$emit("files-changed", this.files);
    },

    iconFor(file) {
      const name = (file.name || "").toLowerCase();
      if (name.endsWith(".csv")) return "mdi-file-delimited";
      if (name.endsWith(".pdf")) return "mdi-file-pdf-box";
      return "mdi-file";
    },
  },
};
</script>

<style>
.dropzone-container {
  padding: 4rem;
  background: #f7fafc;
  border: 3px dashed;
  border-color: #9e9e9e;
}

.hidden-input {
  opacity: 0;
  overflow: hidden;
  position: absolute;
  width: 1px;
  height: 1px;
}

.file-label {
  font-size: 25px;
  display: block;
  cursor: pointer;
}

.preview-container {
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
}

.preview-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 2px solid #a2a2a2;
  border-radius: 6px;
  padding: 6px;
  margin-top: 6px;
}
</style>
